/**
 * ABC Curve Pricing Tab - Calculate prices based on sales volume
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert, HelpTooltip, HelpSteps } from '../../components/ui/HelpGuide';
import { Modal } from '../../components/ui/modal';
import { 
  TrendingUp, 
  CheckCircle,
  ArrowRight,
  Zap,
  Flame,
  Snowflake
} from 'lucide-react';

const TIER_COLORS = {
  A: 'bg-blue-500',
  B: 'bg-yellow-500',
  C: 'bg-green-500'
};

const TIER_LABELS = {
  A: { name: 'Baixa Rotação', icon: Snowflake, color: 'text-blue-500' },
  B: { name: 'Média Rotação', icon: Flame, color: 'text-yellow-500' },
  C: { name: 'Alta Rotação', icon: Zap, color: 'text-green-500' }
};

export default function ABCCurvePricing({ categories, formatCurrency }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  
  const [categoryId, setCategoryId] = useState('all');
  const [tierAMargin, setTierAMargin] = useState(50);
  const [tierBMargin, setTierBMargin] = useState(35);
  const [tierCMargin, setTierCMargin] = useState(20);
  
  const [calculations, setCalculations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState({});
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleCalculate = async () => {
    try {
      setLoading(true);
      
      const payload = {
        tier_a_margin: tierAMargin,
        tier_b_margin: tierBMargin,
        tier_c_margin: tierCMargin
      };
      
      if (categoryId && categoryId !== 'all') {
        payload.category_id = categoryId;
      }
      
      const response = await api.post('/pricing/calculate/abc-curve', payload);
      
      if (response.data?.success) {
        const calcs = response.data.data.calculations || [];
        setCalculations(calcs);
        setSummary(response.data.data.summary || null);
        setSelectedProducts(calcs.map(c => c.product_id));
        
        // Set default tier selection
        const defaultTiers = {};
        calcs.forEach(c => {
          defaultTiers[c.product_id] = c.tier;
        });
        setSelectedTiers(defaultTiers);
        
        toast.success(`${calcs.length} produtos classificados`);
      }
    } catch (err) {
      toast.error('Erro ao calcular curva ABC');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleTierChange = (productId, tier) => {
    setSelectedTiers(prev => ({ ...prev, [productId]: tier }));
  };

  const handleApplyPrices = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    try {
      setApplying(true);
      
      const calcsToApply = calculations
        .filter(c => selectedProducts.includes(c.product_id))
        .map(c => {
          const tier = selectedTiers[c.product_id] || c.tier;
          return {
            product_id: c.product_id,
            new_price: c.suggested_prices[tier]
          };
        });
      
      const response = await api.post('/pricing/apply', {
        calculations: calcsToApply
      });
      
      if (response.data?.success) {
        toast.success(`${response.data.data.updated_count} preços atualizados!`);
        setShowConfirmModal(false);
        setCalculations([]);
        setSummary(null);
      }
    } catch (err) {
      toast.error('Erro ao aplicar preços');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6">
      <HelpAlert variant="guide" title="Curva ABC">
        <HelpSteps
          steps={[
            { description: "Produtos de ALTA rotação (vendem muito) recebem margem MENOR para manter competitividade" },
            { description: "Produtos de BAIXA rotação (vendem pouco) recebem margem MAIOR para compensar o giro lento" },
            { description: "Você pode aceitar a sugestão do sistema ou escolher outra margem para cada produto" }
          ]}
        />
      </HelpAlert>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurar Margens por Curva</CardTitle>
          <CardDescription>
            Defina a margem de lucro para cada classificação ABC
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Category Filter */}
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Categorias</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Margin Sliders */}
          <div className="grid grid-cols-3 gap-6">
            {/* Tier A */}
            <div className="space-y-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Snowflake className="h-5 w-5 text-blue-500" />
                  <Label>Curva A (Baixa Rotação)</Label>
                </div>
                <Badge className="bg-blue-500">{tierAMargin}%</Badge>
              </div>
              <Slider
                value={[tierAMargin]}
                onValueChange={([v]) => setTierAMargin(v)}
                min={10}
                max={100}
                step={5}
              />
              <p className="text-xs text-muted-foreground">
                Produtos que vendem menos - margem maior
              </p>
            </div>

            {/* Tier B */}
            <div className="space-y-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-yellow-500" />
                  <Label>Curva B (Média Rotação)</Label>
                </div>
                <Badge className="bg-yellow-500">{tierBMargin}%</Badge>
              </div>
              <Slider
                value={[tierBMargin]}
                onValueChange={([v]) => setTierBMargin(v)}
                min={10}
                max={80}
                step={5}
              />
              <p className="text-xs text-muted-foreground">
                Produtos de venda mediana
              </p>
            </div>

            {/* Tier C */}
            <div className="space-y-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-green-500" />
                  <Label>Curva C (Alta Rotação)</Label>
                </div>
                <Badge className="bg-green-500">{tierCMargin}%</Badge>
              </div>
              <Slider
                value={[tierCMargin]}
                onValueChange={([v]) => setTierCMargin(v)}
                min={5}
                max={60}
                step={5}
              />
              <p className="text-xs text-muted-foreground">
                Produtos campeões de venda - margem competitiva
              </p>
            </div>
          </div>

          <Button 
            onClick={handleCalculate} 
            disabled={loading}
            className="w-full"
            data-testid="calculate-abc-btn"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            {loading ? 'Analisando vendas...' : 'Calcular Curva ABC'}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {calculations.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Snowflake className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{summary?.tier_a_count || 0}</div>
                  <div className="text-xs text-muted-foreground">Curva A ({tierAMargin}%)</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Flame className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{summary?.tier_b_count || 0}</div>
                  <div className="text-xs text-muted-foreground">Curva B ({tierBMargin}%)</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{summary?.tier_c_count || 0}</div>
                  <div className="text-xs text-muted-foreground">Curva C ({tierCMargin}%)</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary?.total_products || 0}</div>
                <div className="text-xs text-muted-foreground">Total de Produtos</div>
              </CardContent>
            </Card>
          </div>

          {/* Products Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Classificação ABC</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y max-h-96 overflow-y-auto">
                {calculations.map((calc) => {
                  const TierIcon = TIER_LABELS[calc.tier].icon;
                  const selectedTier = selectedTiers[calc.product_id] || calc.tier;
                  
                  return (
                    <div key={calc.product_id} className="flex items-center gap-4 py-3">
                      <Checkbox
                        checked={selectedProducts.includes(calc.product_id)}
                        onCheckedChange={() => handleSelectProduct(calc.product_id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{calc.product_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {calc.sales_quantity} vendas ({calc.sales_percentage}%)
                        </div>
                      </div>
                      <Badge className={`${TIER_COLORS[calc.tier]} text-white`}>
                        <TierIcon className="h-3 w-3 mr-1" />
                        Curva {calc.tier}
                      </Badge>
                      <div className="text-center w-24">
                        <div className="text-xs text-muted-foreground">Custo</div>
                        <div className="text-sm">{formatCurrency(calc.total_cost)}</div>
                      </div>
                      
                      {/* Tier Selection */}
                      <div className="flex items-center gap-1">
                        {['A', 'B', 'C'].map(tier => (
                          <button
                            key={tier}
                            onClick={() => handleTierChange(calc.product_id, tier)}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              selectedTier === tier
                                ? `${TIER_COLORS[tier]} text-white`
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {tier}: {formatCurrency(calc.suggested_prices[tier])}
                          </button>
                        ))}
                      </div>
                      
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <div className="text-center w-24">
                        <div className="text-xs text-muted-foreground">Atual</div>
                        <div className="text-sm">{formatCurrency(calc.current_price)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Apply Button */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <span className="font-medium">{selectedProducts.length}</span> produtos selecionados
              </div>
              <Button 
                onClick={() => setShowConfirmModal(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aplicar Preços Selecionados
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm Modal */}
      <Modal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirmar Aplicação de Preços"
        confirmText="Confirmar e Aplicar"
        loading={applying}
        onConfirm={handleApplyPrices}
      >
        <HelpAlert variant="info">
          Os preços serão atualizados de acordo com a margem selecionada para cada produto.
        </HelpAlert>
      </Modal>
    </div>
  );
}
